import React from 'react';
import { Card, Button } from 'react-bootstrap';
import { FaLock, FaArrowUp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { usePortalAccess } from '../../hooks/usePortalAccess';
import { ACCOUNT_CHANGE_PLAN_PATH } from '../../utils/portalAccess';
import styles from './UpgradePrompt.module.scss';

/**
 * UpgradePrompt — the locked-feature card every gate funnels through
 * (`FeatureGate`, `FeatureProtectedRoute`, `AnalyticsTabGate`).
 *
 * TASK 112 — the owner's decision of 2026-11-04:
 *
 *  1. the ACCOUNT user (role 1/2: the owner/admin of a Brand or a Multi-Vendor
 *     account) KEEPS the padlock and the upgrade action, and that action now
 *     points at a page that EXISTS for them —
 *     `/admin/subscription-billing/change-plan` (route: src/App.js). The old
 *     hardcoded `/admin/subscription-billing/change-tier` matched no route at
 *     all and 404'd; nothing may ever link there again.
 *
 *  2. the SELLER user (role 3: a store under a mall, a multi-vendor seller, a
 *     branch) is never sold anything here. No padlock, no upgrade button, no
 *     "Upgrade Plan": just a plain "not available for your role" card that
 *     points at their account admin. Upgrading is the account user's business.
 *     The same branch also covers an unidentified viewer — an upsell is only
 *     ever shown to a viewer we have positively identified as the account user.
 */
const UpgradePrompt = ({
  featureName,
  description,
  buttonText = 'Upgrade Plan',
  onUpgradeClick,
  upgradePath,
  className = '',
  size = 'default', // "default", "small", "large"
}) => {
  const { t } = useTranslation('common');
  const { canOfferUpgrade, upgradePath: roleUpgradePath } = usePortalAccess();

  const sizeClasses = {
    small: styles.upgradePromptSmall,
    default: styles.upgradePromptDefault,
    large: styles.upgradePromptLarge,
  };

  // ── SELLER (or unidentified viewer): the deliberate, quiet refusal ──
  if (!canOfferUpgrade) {
    return (
      <Card
        className={`${styles.upgradePrompt} ${sizeClasses[size]} ${className}`}
        data-locked-viewer="seller"
      >
        <Card.Body className={styles.upgradePromptBody}>
          <div className={styles.upgradePromptContent}>
            <h5 className={styles.upgradePromptTitle}>
              {t('upgrade.roleRestrictedTitle', { defaultValue: 'Not available for your role' })}
            </h5>
            <p className={styles.upgradePromptDescription}>
              {t('upgrade.roleRestrictedBody', {
                defaultValue:
                  'This capability is managed by your account admin. Ask them to enable it for your account.',
              })}
            </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // ── ACCOUNT user: padlock + an upgrade action that lands on a real page ──
  const destination = upgradePath || roleUpgradePath || ACCOUNT_CHANGE_PLAN_PATH;

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
      return;
    }
    window.location.assign(destination);
  };

  return (
    <Card className={`${styles.upgradePrompt} ${sizeClasses[size]} ${className}`}>
      <Card.Body className={styles.upgradePromptBody}>
        <div className={styles.upgradePromptIcon}>
          <FaLock data-lock-icon="padlock" />
        </div>
        <div className={styles.upgradePromptContent}>
          <h5 className={styles.upgradePromptTitle}>
            {featureName} is not available
          </h5>
          <p className={styles.upgradePromptDescription}>
            {description || `Upgrade your plan to access ${featureName} and unlock more powerful features.`}
          </p>
          <Button
            variant="primary"
            onClick={handleUpgradeClick}
            className={styles.upgradePromptButton}
            data-upgrade-path={destination}
          >
            <FaArrowUp className="me-2" />
            {buttonText}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default UpgradePrompt;
